import React from "react";
import i18n from "../../../i18n";
import { Box, Typography } from "@mui/material";
import YouTubeShortsIcon from "../../../components/icons/YouTubeShortsIcon";
import type { PodcastMediaKit } from "../../../api/podcasts";
import {
  fmtEngagement,
  fmtMetric,
  parsePlatformMetrics,
} from "./buildPlatformMetrics";
import {
  instagramIcon,
  facebookIcon,
  tiktokIcon,
  youtubeIcon,
} from "./platformPanels/audienceCards";
import {
  EMPTY_CELL,
  type MetricsTableColumn,
  type MetricsTableGroup,
  type MetricsTableRow,
} from "./MetricsComparisonTable";
import { METRICS_TABLE_STREAM_COL_MIN_WIDTH } from "./metricsTableLayout";

type Audience =
  | {
      gender?: Array<{ type: string; confidence?: number; percentage?: number }>;
      ageRanges?: Array<{ range: string; confidence?: number; percentage?: number }>;
      selfReported?: boolean;
    }
  | null
  | undefined;

const num = (n: number | null | undefined): string =>
  n != null ? fmtMetric(n) : EMPTY_CELL;

type IgReach = NonNullable<
  NonNullable<PodcastMediaKit["analytics"]>["instagram"]
>["reach"];

function splitIgInteractions(reach: IgReach | undefined): {
  reels: number | null;
  posts: number | null;
} {
  if (!reach) return { reels: null, posts: null };

  const reelsDirect =
    reach.reelsInteractions != null && reach.reelsInteractions > 0
      ? reach.reelsInteractions
      : null;
  const postsDirect =
    reach.postsInteractions != null && reach.postsInteractions > 0
      ? reach.postsInteractions
      : null;
  if (reelsDirect != null || postsDirect != null) {
    return { reels: reelsDirect, posts: postsDirect };
  }

  const total = reach.totalInteractions;
  if (total == null || total <= 0) return { reels: null, posts: null };

  const reelsReach = igReachValue(reach, "reels") ?? 0;
  const postsReach = igReachValue(reach, "posts") ?? 0;
  const reachSum = reelsReach + postsReach;
  if (reachSum <= 0) return { reels: null, posts: null };

  return {
    reels: Math.round((total * reelsReach) / reachSum),
    posts: Math.round((total * postsReach) / reachSum),
  };
}

function igSplitMetric(
  reach: IgReach | undefined,
  kind: "reels" | "posts",
  metric: "views" | "reach",
): number | null {
  if (!reach) return null;

  const direct =
    metric === "views"
      ? kind === "reels"
        ? reach.reelsViews
        : reach.postsViews
      : kind === "reels"
        ? reach.reelsReach
        : reach.postsReach;
  if (direct != null && direct > 0) return direct;

  const reelsCount = reach.reelsCount ?? 0;
  const postsCount = reach.postsCount ?? 0;
  const total =
    metric === "views" ? reach.totalViews : reach.totalReach;
  const count = kind === "reels" ? reelsCount : postsCount;

  if (total != null && total > 0 && count > 0) {
    if (kind === "reels" && postsCount === 0) return total;
    if (kind === "posts" && reelsCount === 0) return total;
  }

  return null;
}

function igViewsValue(
  reach: IgReach | undefined,
  kind: "reels" | "posts",
): number | null {
  return igSplitMetric(reach, kind, "views");
}

function igAvgViewsValue(
  reach: IgReach | undefined,
  kind: "reels" | "posts",
): number | null {
  if (!reach) return null;

  const direct =
    kind === "reels" ? reach.avgViewsPerReel : reach.avgViewsPerPost;
  if (direct != null && direct > 0) return direct;

  const total = igViewsValue(reach, kind);
  const count =
    kind === "reels" ? (reach.reelsCount ?? 0) : (reach.postsCount ?? 0);
  if (total != null && total > 0 && count > 0) {
    return Math.round(total / count);
  }

  return null;
}

function igReachValue(
  reach: IgReach | undefined,
  kind: "reels" | "posts",
): number | null {
  return igSplitMetric(reach, kind, "reach");
}

function igAvgReachValue(
  reach: IgReach | undefined,
  kind: "reels" | "posts",
): number | null {
  if (!reach) return null;

  const direct =
    kind === "reels" ? reach.avgReachPerReel : reach.avgReachPerPost;
  if (direct != null && direct > 0) return direct;

  const total = igReachValue(reach, kind);
  const count =
    kind === "reels" ? (reach.reelsCount ?? 0) : (reach.postsCount ?? 0);
  if (total != null && total > 0 && count > 0) {
    return Math.round(total / count);
  }

  return null;
}

function igEngagement(
  interactions: number | null,
  reachValue: number | null | undefined,
): number | null {
  if (interactions == null || reachValue == null || reachValue <= 0) return null;
  return Math.round((interactions / reachValue) * 10000) / 100;
}

function genderLabel(type: string): string {
  if (type === "male" || type === "female" || type === "unknown") {
    return i18n.t(`miContenido:gender.${type}`);
  }
  return type;
}

const GENDER_ORDER = ["female", "male", "unknown"];

const GENDER_COLORS: Record<string, string> = {
  male: "#4D9EF6",
  female: "#F7A6B2",
  unknown: "#6A89CC",
};

const AGE_COLORS = [
  "#667eea",
  "#764ba2",
  "#f093fb",
  "#4facfe",
  "#43e97b",
  "#fa709a",
  "#fee140",
];

function ageBarColor(idx: number): string {
  return AGE_COLORS[idx % AGE_COLORS.length] ?? "#667eea";
}

function demographicPct(row?: {
  confidence?: number;
  percentage?: number;
}): number | null {
  if (!row) return null;
  const v = row.percentage ?? row.confidence;
  return v == null ? null : v;
}

function formatAgeRangeLabel(range: string): string {
  const cleaned = range.replace(/^age/i, "").trim();
  return cleaned === "65-" ? "65+" : cleaned;
}

function genderPctFor(aud: Audience, type: string): number | null {
  const row = (aud?.gender ?? []).find(
    (r) => r.type.trim().toLowerCase() === type,
  );
  return demographicPct(row);
}

function agePctFor(aud: Audience, label: string): number | null {
  const row = (aud?.ageRanges ?? []).find(
    (r) => formatAgeRangeLabel(r.range) === label,
  );
  return demographicPct(row);
}

/** Drops columns whose every value is empty. Rows with partial empty cells are kept. */
function pruneEmptyColumns(
  columns: MetricsTableColumn[],
  rows: MetricsTableRow[],
): { columns: MetricsTableColumn[]; rows: MetricsTableRow[] } {
  const keep = columns.map((_, idx) =>
    rows.some((row) => row.values[idx] !== EMPTY_CELL),
  );
  const nextColumns = columns.filter((_, idx) => keep[idx]);
  const nextRows = rows.map((row) => ({
    ...row,
    values: row.values.filter((_, idx) => keep[idx]),
  }));
  return { columns: nextColumns, rows: nextRows };
}

export type ComparisonTableConfig = {
  columns: MetricsTableColumn[];
  groups: MetricsTableGroup[];
};

export function buildContentTable(
  analytics: PodcastMediaKit["analytics"],
): ComparisonTableConfig | null {
  const parsed = parsePlatformMetrics(analytics);
  const yt = analytics?.youtube;
  const tt = analytics?.tiktok;
  const reach = analytics?.instagram?.reach;

  const columns: MetricsTableColumn[] = [
    { id: "yt-videos", label: i18n.t("miContenido:platforms.youtube"), icon: youtubeIcon },
    {
      id: "yt-shorts",
      label: i18n.t("miContenido:platforms.youtube_shorts"),
      icon: <YouTubeShortsIcon sx={{ fontSize: 22, color: "#FF0000" }} />,
    },
    { id: "ig-reels", label: i18n.t("miContenido:platforms.igReels"), icon: instagramIcon },
    { id: "ig-posts", label: i18n.t("miContenido:platforms.igPosts"), icon: instagramIcon },
    { id: "tiktok", label: i18n.t("miContenido:platforms.tiktok"), icon: tiktokIcon },
    { id: "facebook", label: i18n.t("miContenido:platforms.facebook"), icon: facebookIcon },
  ];

  const ytLongInteractions =
    yt?.longForm?.consumption?.totalInteractions ??
    yt?.consumption?.totalInteractions ??
    null;
  const ytShortsInteractions = yt?.shorts?.consumption?.totalInteractions ?? null;
  const ttInteractions = tt?.consumption?.totalInteractions ?? null;
  const fbInteractions =
    analytics?.facebook?.consumption?.totalInteractions ?? null;
  const igInteractions = splitIgInteractions(reach);
  const igReelsInteractions = igInteractions.reels;
  const igPostsInteractions = igInteractions.posts;

  const igReelsViews = igViewsValue(reach, "reels");
  const igPostsViews = igViewsValue(reach, "posts");
  const igReelsReach = igReachValue(reach, "reels");
  const igPostsReach = igReachValue(reach, "posts");
  const igReelsAvgViews = igAvgViewsValue(reach, "reels");
  const igPostsAvgViews = igAvgViewsValue(reach, "posts");
  const igReelsAvgReach = igAvgReachValue(reach, "reels");
  const igPostsAvgReach = igAvgReachValue(reach, "posts");

  const igReelsEngagement = igEngagement(igReelsInteractions, igReelsReach);
  const igPostsEngagement = igEngagement(igPostsInteractions, igPostsReach);

  /** YouTube Videos + YouTube Shorts — IG-only rows leave these blank. */
  const ytOnlyEmpty: [string, string] = [EMPTY_CELL, EMPTY_CELL];

  const rows: MetricsTableRow[] = [
    {
      key: "views",
      label: i18n.t("miContenido:metrics.views"),
      values: [
        num(parsed.youtubeLong.views),
        num(parsed.youtubeShorts.views),
        num(igReelsViews),
        num(igPostsViews),
        num(parsed.tiktok.views),
        num(parsed.facebook.views),
      ],
    },
    {
      key: "reach",
      label: i18n.t("miContenido:metrics.reach"),
      values: [
        ...ytOnlyEmpty,
        num(igReelsReach),
        num(igPostsReach),
        EMPTY_CELL,
        EMPTY_CELL,
      ],
    },
    {
      key: "content",
      label: i18n.t("miContenido:metrics.contents"),
      values: [
        num(parsed.youtubeLong.contentCount),
        num(parsed.youtubeShorts.contentCount),
        num(reach?.reelsCount),
        num(reach?.postsCount),
        num(parsed.tiktok.contentCount),
        num(parsed.facebook.contentCount),
      ],
    },
    {
      key: "views-average",
      label: i18n.t("miContenido:metrics.avgViewsLong"),
      values: [
        num(parsed.youtubeLong.avgViews),
        num(parsed.youtubeShorts.avgViews),
        num(igReelsAvgViews),
        num(igPostsAvgViews),
        num(parsed.tiktok.avgViews),
        num(parsed.facebook.avgViews),
      ],
    },
    {
      key: "reach-average",
      label: i18n.t("miContenido:metrics.avgReach"),
      values: [
        ...ytOnlyEmpty,
        num(igReelsAvgReach),
        num(igPostsAvgReach),
        EMPTY_CELL,
        EMPTY_CELL,
      ],
    },
    {
      key: "interactions",
      label: i18n.t("miContenido:metrics.interactions"),
      values: [
        num(ytLongInteractions),
        num(ytShortsInteractions),
        num(igReelsInteractions),
        num(igPostsInteractions),
        num(ttInteractions),
        num(fbInteractions),
      ],
    },
    {
      key: "engagement",
      label: i18n.t("miContenido:metrics.engagement"),
      values: [
        fmtEngagement(parsed.youtubeLong.engagementPercent),
        fmtEngagement(parsed.youtubeShorts.engagementPercent),
        fmtEngagement(igReelsEngagement),
        fmtEngagement(igPostsEngagement),
        fmtEngagement(parsed.tiktok.engagementPercent),
        fmtEngagement(parsed.facebook.engagementPercent),
      ],
    },
  ];

  const pruned = pruneEmptyColumns(columns, rows);
  if (pruned.columns.length === 0 || pruned.rows.length === 0) return null;

  return { columns: pruned.columns, groups: [{ key: "content", rows: pruned.rows }] };
}

const podscopeIcon = (
  <Box
    sx={{
      width: 22,
      height: 22,
      flexShrink: 0,
      borderRadius: 0.5,
      boxSizing: "border-box",
      border: "1px solid",
      borderColor: "divider",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: "background.paper",
    }}
  >
    <Typography
      component="span"
      sx={{
        fontWeight: 700,
        lineHeight: 1,
        fontSize: "0.8125rem",
        color: "primary.main",
      }}
    >
      P
    </Typography>
  </Box>
);

export type PodscopeTableConfig = ComparisonTableConfig & {
  minDataColWidth?: number;
  /** Pin Promedio while emission cols scroll (desktop only). */
  stickyLeadingDataColumns?: number;
};

type PodscopeStreamSnapshotRow = NonNullable<
  NonNullable<
    NonNullable<PodcastMediaKit["analytics"]>["podscope"]
  >["streamingFromSnapshots"]
>["streams"] extends (infer R)[] | undefined
  ? R
  : never;

const streamDateFormatter = new Intl.DateTimeFormat("es-CL", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

function formatStreamColumnDate(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return streamDateFormatter.format(d);
}

function buildPodscopePerStreamTable(
  analytics: PodcastMediaKit["analytics"],
  streams: PodscopeStreamSnapshotRow[],
): PodscopeTableConfig | null {
  if (!streams.length) return null;

  const ps = analytics?.podscope;
  const snap = ps?.streamingFromSnapshots;
  const stream = ps?.streaming;
  const { podscopeCard, snapshotStreamingActive } = parsePlatformMetrics(analytics);

  const summaryAvgViewers = snapshotStreamingActive
    ? podscopeCard.promEspectadores
    : snap?.avgViewers ?? stream?.avgViewers ?? null;
  const summaryPeakViewers = snapshotStreamingActive
    ? podscopeCard.peakEspectadores
    : snap?.peakViewers ?? stream?.peakViewers ?? null;
  const summaryAvgChat = snapshotStreamingActive
    ? podscopeCard.promMensajes
    : stream?.chatMessagesAvg ?? snap?.avgChatMessages ?? null;
  const summaryAvgChatUsers = snapshotStreamingActive
    ? podscopeCard.promEspChat
    : stream?.uniqueChatUsersAvg ?? snap?.avgChatUniqueUsers ?? null;

  const columns: MetricsTableColumn[] = [
    { id: "promedio", label: i18n.t("miContenido:metrics.average"), icon: podscopeIcon },
    ...streams.map((s) => ({
      id: s.streamId,
      label: s.title,
      subtitle: formatStreamColumnDate(s.startedAt),
    })),
  ];

  const rowIfAny = (
    key: string,
    label: string,
    summary: number | null | undefined,
    pick: (s: PodscopeStreamSnapshotRow) => number | null | undefined,
  ): MetricsTableRow | null => {
    const perStreamValues = streams.map((s) => num(pick(s)));
    const summaryValue = num(summary);
    if (summaryValue === EMPTY_CELL && !perStreamValues.some((v) => v !== EMPTY_CELL)) {
      return null;
    }
    return { key, label, values: [summaryValue, ...perStreamValues] };
  };

  const rows = [
    rowIfAny("avg-viewers", i18n.t("miContenido:metrics.avgViewers"), summaryAvgViewers, (s) => s.avgViewers),
    rowIfAny("peak-viewers", i18n.t("miContenido:metrics.peakViewers"), summaryPeakViewers, (s) => s.peakViewers),
    rowIfAny(
      "chat-messages",
      i18n.t("miContenido:metrics.chatMessages"),
      summaryAvgChat,
      (s) => (s.chatMessages != null && s.chatMessages > 0 ? s.chatMessages : null),
    ),
    rowIfAny(
      "chat-users",
      i18n.t("miContenido:metrics.uniqueChat"),
      summaryAvgChatUsers,
      (s) =>
        s.chatUniqueUsers != null && s.chatUniqueUsers > 0
          ? s.chatUniqueUsers
          : null,
    ),
  ].filter((r): r is MetricsTableRow => r != null);

  if (rows.length === 0) return null;

  return {
    columns,
    groups: [{ key: "streaming", rows }],
    minDataColWidth: METRICS_TABLE_STREAM_COL_MIN_WIDTH,
    stickyLeadingDataColumns: 1,
  };
}

/** Livestream metrics: one column per emission when available, else aggregate summary. */
export function buildPodscopeTable(
  analytics: PodcastMediaKit["analytics"],
): PodscopeTableConfig | null {
  const ps = analytics?.podscope;
  if (!ps || Object.keys(ps).length === 0) return null;

  const streams = ps.streamingFromSnapshots?.streams;
  if (streams?.length) {
    const perStream = buildPodscopePerStreamTable(analytics, streams);
    if (perStream) return perStream;
  }

  const { podscopeCard, snapshotStreamingActive } = parsePlatformMetrics(analytics);
  const stream = ps.streaming;
  const snap = ps.streamingFromSnapshots;

  const rows: MetricsTableRow[] = [];

  const streamCount = snapshotStreamingActive
    ? podscopeCard.streamCount
    : snap?.streamCount != null && snap.streamCount > 0
      ? snap.streamCount
      : null;
  if (streamCount != null) {
    rows.push({ key: "streams", label: i18n.t("miContenido:sections.livestreams"), values: [num(streamCount)] });
  }

  const avgViewers = snapshotStreamingActive
    ? podscopeCard.promEspectadores
    : stream?.avgViewers ?? snap?.avgViewers ?? null;
  if (avgViewers != null) {
    rows.push({
      key: "avg-viewers",
      label: i18n.t("miContenido:metrics.avgViewers"),
      values: [num(avgViewers)],
    });
  }

  const peakViewers = snapshotStreamingActive
    ? podscopeCard.peakEspectadores
    : stream?.peakViewers ?? snap?.peakViewers ?? null;
  if (peakViewers != null) {
    rows.push({
      key: "peak-viewers",
      label: i18n.t("miContenido:metrics.peakViewers"),
      values: [num(peakViewers)],
    });
  }

  if (!snapshotStreamingActive && stream?.chatMessages != null && stream.chatMessages > 0) {
    rows.push({
      key: "chat-total",
      label: i18n.t("miContenido:metrics.chatMessages"),
      values: [num(stream.chatMessages)],
    });
  }

  const avgChat = snapshotStreamingActive
    ? podscopeCard.promMensajes
    : stream?.chatMessagesAvg ?? snap?.avgChatMessages ?? null;
  if (avgChat != null) {
    rows.push({
      key: "avg-chat",
      label: i18n.t("miContenido:metrics.chatMessagesAvg"),
      values: [num(avgChat)],
    });
  }

  const avgChatUsers = snapshotStreamingActive
    ? podscopeCard.promEspChat
    : stream?.uniqueChatUsersAvg ?? snap?.avgChatUniqueUsers ?? null;
  if (avgChatUsers != null) {
    rows.push({
      key: "avg-chat-users",
      label: i18n.t("miContenido:metrics.uniqueChatAvg"),
      values: [num(avgChatUsers)],
    });
  }

  if (ps.programRank != null) {
    rows.push({
      key: "program-rank",
      label: i18n.t("miContenido:metrics.programRank"),
      values: [`#${ps.programRank}`],
    });
  }

  if (rows.length === 0) return null;

  return {
    columns: [{ id: "livestreams", label: i18n.t("miContenido:sections.livestreams"), icon: podscopeIcon }],
    groups: [{ key: "streaming", rows }],
  };
}

export type DemographicsCellValue = number | null;

export type DemographicsCellFormat = "percent" | "count";

export type DemographicsTableRow = {
  key: string;
  label: string;
  values: DemographicsCellValue[];
  /** Per-row bar color (e.g. geography uses a distinct color per location). */
  color?: string;
  /** How to render cell values. Defaults to proportional bars (percent). */
  format?: DemographicsCellFormat;
};

export type DemographicsTableGroup = {
  key: string;
  label: string;
  rows: DemographicsTableRow[];
};

export type DemographicsTableData = {
  columns: MetricsTableColumn[];
  groups: DemographicsTableGroup[];
  selfReportedNote: boolean;
};

/** Drops columns with no values across all rows, and rows that become all-null. */
function pruneNumericColumns(
  columns: MetricsTableColumn[],
  rows: DemographicsTableRow[],
): { columns: MetricsTableColumn[]; rows: DemographicsTableRow[] } {
  const keep = columns.map((_, idx) =>
    rows.some((row) => row.values[idx] != null),
  );
  const nextColumns = columns.filter((_, idx) => keep[idx]);
  const nextRows = rows
    .map((row) => ({
      ...row,
      values: row.values.filter((_, idx) => keep[idx]),
    }))
    .filter((row) => row.values.some((v) => v != null));
  return { columns: nextColumns, rows: nextRows };
}

export function buildDemographicsTable(
  analytics: PodcastMediaKit["analytics"],
  statistics?: PodcastMediaKit["statistics"],
): DemographicsTableData | null {
  const yt = analytics?.youtube;
  const ytAudience = yt?.longForm?.audience ?? yt?.audience;
  const igAudience = analytics?.instagram?.audience;
  const ttAudience = analytics?.tiktok?.audience;

  const columns: MetricsTableColumn[] = [
    { id: "youtube", label: i18n.t("miContenido:platforms.youtubeBrand"), icon: youtubeIcon },
    { id: "instagram", label: i18n.t("miContenido:platforms.instagram"), icon: instagramIcon },
    { id: "tiktok", label: i18n.t("miContenido:platforms.tiktok"), icon: tiktokIcon },
  ];
  const audiences: Audience[] = [ytAudience, igAudience, ttAudience];

  const followerCount = (n: number | null | undefined): number | null =>
    n != null && n > 0 ? n : null;

  const followersRow: DemographicsTableRow = {
    key: "followers",
    label: i18n.t("miContenido:metrics.followers"),
    values: [
      followerCount(statistics?.youtubeSubscribers),
      followerCount(statistics?.instagramFollowers),
      followerCount(statistics?.tiktokFollowers),
    ],
    format: "count",
  };

  const genderRows: DemographicsTableRow[] = GENDER_ORDER.map((type) => ({
    key: `gender-${type}`,
    label: genderLabel(type),
    values: audiences.map((aud) => genderPctFor(aud, type)),
    color: GENDER_COLORS[type] ?? "#667eea",
  }));

  const ageLabels = Array.from(
    new Set(
      audiences.flatMap((aud) =>
        (aud?.ageRanges ?? []).map((r) => formatAgeRangeLabel(r.range)),
      ),
    ),
  ).sort((a, b) => {
    const aStart = parseInt(a.split("-")[0] ?? "0", 10);
    const bStart = parseInt(b.split("-")[0] ?? "0", 10);
    return aStart - bStart;
  });

  const ageRows: DemographicsTableRow[] = ageLabels.map((label, idx) => ({
    key: `age-${label}`,
    label,
    values: audiences.map((aud) => agePctFor(aud, label)),
    color: ageBarColor(idx),
  }));

  const pruned = pruneNumericColumns(columns, [
    followersRow,
    ...genderRows,
    ...ageRows,
  ]);
  if (pruned.columns.length === 0 || pruned.rows.length === 0) return null;

  const keptFollowerRows = pruned.rows.filter((r) => r.key === "followers");
  const keptGenderRows = pruned.rows.filter((r) => r.key.startsWith("gender-"));
  const keptAgeRows = pruned.rows.filter((r) => r.key.startsWith("age-"));

  const groups: DemographicsTableGroup[] = [];
  if (keptFollowerRows.length > 0) {
    groups.push({ key: "account", label: i18n.t("miContenido:metrics.account"), rows: keptFollowerRows });
  }
  if (keptGenderRows.length > 0)
    groups.push({ key: "gender", label: i18n.t("miContenido:metrics.gender"), rows: keptGenderRows });
  if (keptAgeRows.length > 0)
    groups.push({ key: "age", label: i18n.t("miContenido:metrics.age"), rows: keptAgeRows });

  const keptTikTok = pruned.columns.some((c) => c.id === "tiktok");

  return {
    columns: pruned.columns,
    groups,
    selfReportedNote: Boolean(keptTikTok && ttAudience?.selfReported),
  };
}

export function DemographicsSelfReportedNote() {
  return (
    <Typography variant="caption" color="text.secondary">
      TikTok: datos demográficos autoreportados.
    </Typography>
  );
}
